import React, { useState, useMemo, useEffect } from 'react';
import { ChevronDown, MessageSquare, FileText, TrendingUp, Building2, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { LeadRecord } from '../data/mockData';
import { TaggedLender } from '../data/lenderData';
import { cn } from '../../lib/utils';
import { CreditRatingBadge, DocumentStatusBadge, LoginStatusBadge, ProductsCell, PriorityDropdown, StatusDropdown, CustomerStatusOption } from './TableCells';
import { format, isValid } from 'date-fns';
import { Button } from '../../components/ui/button';
import CommentPopup from '../../components/CommentPopup';
import { Comment, CommentIcon } from '../../types/prospect';
import apiService from '../../services/apiService';
import { LenderStatusDialog, StatusFormValues } from '../../components/requirementss/LenderStatusDialog';
import { useNavigate } from 'react-router-dom';
import { ChevronUp, ChevronsUpDown } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { Badge } from '../../components/ui/badge';
import { getStatusConfig } from '../../utils/statusConfig';
import { SortField, SortOrder } from './sort';
import { useCustomerWorkflow } from '../../context/CustomerWorkflowContext';

interface AdminUserList {
  id: number;
  name: string;
};

interface CreditEvaluationTableProps {
  data: LeadRecord[];
  statusOptions: CustomerStatusOption[];
  onRowClick: (record: LeadRecord) => void;
  onInteractionClick: (record: LeadRecord) => void;
  onUpdateRecord?: (id: string, updates: Partial<LeadRecord>) => void;
  adminUserList?: AdminUserList[];
  sortField?: SortField;
  sortOrder?: SortOrder;
  onSortChange?: (field: SortField) => void;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

function formatDate(value: Date | string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';

  const parsedDate = value instanceof Date ? value : new Date(value);
  if (!isValid(parsedDate)) return '—';

  return format(parsedDate, 'MMM dd, yyyy');
}

function truncateText(text: string, maxLength: number): string {
  return text.length <= maxLength ? text : text.slice(0, maxLength) + '...';
}

function getInitials(text: string): string {
  if (!text || typeof text !== 'string') return '';
  const parts = text.trim().split(/\s+/).filter(Boolean);
  const letters =
    parts.length >= 2
      ? `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`
      : (parts[0]?.slice(0, 2) ?? '');
  return letters.toUpperCase();
}

const creditRatingOrder = {
  'AAA': 22, 'AA+': 21, 'AA': 20, 'AA-': 19, 'A+': 18, 'A': 17, 'A-': 16,
  'BBB+': 15, 'BBB': 14, 'BBB-': 13, 'BB+': 12, 'BB': 11, 'BB-': 10,
  'B+': 9, 'B': 8, 'B-': 7, 'CCC+': 6, 'CCC': 5, 'CCC-': 4, 'CC': 3, 'C': 2, 'D': 1
};
const priorityOrder = { High: 3, Medium: 2, Low: 1, '-': 0 };
const loginStatusOrder = { Active: 3, Pending: 2, Inactive: 1 };

function getTextValue(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  return '';
}

function compareTextValues(firstValue: unknown, secondValue: unknown): number {
  return getTextValue(firstValue).localeCompare(getTextValue(secondValue), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function getNumberSortValue(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsedValue = Number(value.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }
  return 0;
}

function getCreatedDateSortValue(value: unknown): number | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.getTime();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const normalizedValue = getTextValue(value);
  if (!normalizedValue) return null;
  const timestamp = Date.parse(normalizedValue);
  return Number.isNaN(timestamp) ? null : timestamp;
}


export function CreditEvaluationTable({
  data,
  statusOptions,
  onRowClick,
  onInteractionClick,
  onUpdateRecord,
  adminUserList,
  sortField,
  sortOrder,
  onSortChange,
}: CreditEvaluationTableProps) {
  const { refreshWorkflow } = useCustomerWorkflow();
  const navigate = useNavigate();
  const [openAccordionId, setOpenAccordionId] = useState<string | null>(null);
  const [commentPopup, setCommentPopup] = useState<{
    isOpen: boolean;
    comments: TaggedLender["comments"];
    lenderId: string | null;
  }>({
    isOpen: false,
    comments: [],
    lenderId: null,
  });
  const [statusOpenFor, setStatusOpenFor] = useState<string | null>(null);
  const [LenderStatus, setLenderStatus] = useState<any>();
  const [lenderStatusOverrides, setLenderStatusOverrides] = useState<
    Record<string, { status?: string; statusId?: string | number }>
  >({});
  const [lenderCommentOverrides, setLenderCommentOverrides] = useState<
    Record<string, TaggedLender["comments"]>
  >({});
  const currentUser =
    `${JSON.parse(localStorage.getItem("detail") || "{}")?.firstName || ""} ${JSON.parse(localStorage.getItem("detail") || "{}")?.lastName || ""}`.trim() ||
    JSON.parse(localStorage.getItem("detail") || "{}")?.name ||
    "You";

  // const [sortKey, setSortKey] = useState<string | null>(null);
  // const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // const handleSort = (key: string) => {
  //   if (sortKey === key) {
  //     setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
  //   } else {
  //     setSortKey(key);
  //     setSortDir('asc');
  //   }
  // };

  const toggleAccordion = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenAccordionId(prev => prev === id ? null : id);
  };
  const handleSaveComment = async (commentText: string, mentions?: string[], icon?: CommentIcon) => {
    if (!commentPopup.lenderId) return;
    const lenderId = commentPopup.lenderId;

    const mentionNames = mentions?.map((mentionId) => {
      const admin = adminUserList?.find((user) => String(user.id) === String(mentionId));
      return admin?.name || mentionId;
    });

    await apiService.post('/customers/create-comment', {
      commentText,
      mentions,
      mentionNames,
      icon,
      id: lenderId,
      type: 'lender',
    });

    const newComment: Comment = {
      id: `temp-${Date.now()}`,
      text: commentText,
      author: currentUser,
      date: new Date().toISOString(),
      mentions: mentionNames,
      icon,
      resolutionStatus: "pending",
    };

    setCommentPopup((prev) => ({
      ...prev,
      comments: [...(prev.comments || []), newComment],
    }));
    setLenderCommentOverrides((prev) => {
      const existingComments = prev[lenderId] ?? commentPopup.comments ?? [];
      return {
        ...prev,
        [lenderId]: [...existingComments, newComment],
      };
    });
  };

  const handleResolveComment = async (commentId: string) => {
    if (!commentPopup.lenderId) return;
    const lenderId = commentPopup.lenderId;

    await apiService.post('/customers/mark-comment-resolved', {
      commentId,
      id: lenderId,
      type: 'lender',
    });

    setCommentPopup((prev) => ({
      ...prev,
      comments: (prev.comments || []).map((comment) =>
        comment.id === commentId ? { ...comment, resolutionStatus: "resolved" } : comment
      ),
    }));
    setLenderCommentOverrides((prev) => {
      const existingComments = prev[lenderId] ?? commentPopup.comments ?? [];
      return {
        ...prev,
        [lenderId]: existingComments.map((comment) =>
          comment.id === commentId ? { ...comment, resolutionStatus: "resolved" } : comment
        ),
      };
    });
  };

  async function handleStatusUpdate(lenderId: string, vals: StatusFormValues) {
    let sanctionLetterUrlFromApi: string | undefined;

    // If sanction letter file is uploaded, call upload API first to get persistent URL
    const sanctionFile = vals.sanctionLetterFile as File | undefined;
    if (sanctionFile && sanctionFile.size > 0) {
      try {
        const formData = new FormData();
        formData.append("file", sanctionFile);
        const response = await apiService.post<{ linkUrl?: string }>(
          `/login/upload-sanction-letter/${lenderId}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        sanctionLetterUrlFromApi = response?.linkUrl ?? (response as any)?.data?.linkUrl;
      } catch (e) {
        console.error("Sanction letter upload failed", e);
      }
    }

    const payload = { ...vals } as StatusFormValues & { sanctionLetterUrl?: string };
    if (sanctionLetterUrlFromApi) payload.sanctionLetterUrl = sanctionLetterUrlFromApi;
    await apiService.post('/login/lender-status-update', { lenderId, vals: payload });
    await refreshWorkflow();
    const selectedStatus = LenderStatus?.find((s: any) => String(s.id) === String(vals.status));
    setLenderStatusOverrides((prev) => ({
      ...prev,
      [lenderId]: {
        status: selectedStatus?.name,
        statusId: selectedStatus?.id,
      },
    }));
    setStatusOpenFor(null);
  }

  // fetch all data on mount
  useEffect(() => {
    const fetchData = async () => {
      // setLoading(true);
      try {
        const [statusRes] = await Promise.all([
          apiService.get<any>("/login/get-lender-status"),
        ]);
        // setLenderUserProductTypes((productRes as any)?.data || productRes || []);
        setLenderStatus((statusRes as any)?.data || statusRes || []);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        // setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Sorting is expected to be handled by parent (server-side). Parent provides `sortField`, `sortOrder` and `onSortChange`.

  // Parent should return sorted data when `sortField`/`sortOrder` are provided.
  const sortedData = data;

  const SortableHeader = ({
    field,
    label,
    className,
    align,
  }: {
    field: SortField;
    label: string;
    className?: string;
    align?: 'right';
  }) => (
    <th className={cn('data-table-cell bg-muted text-left text-[11px] font-medium tracking-wide text-muted-foreground', align === 'right' && 'text-right', className)}>
      <button
        onClick={() => onSortChange?.(field)}
        className={cn('group/sort flex items-center gap-1 transition-colors hover:text-foreground', align === 'right' && 'w-full justify-end')}
      >
        {label}
        {sortField === field ? (
          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-accent-foreground" /> : <ArrowDown className="h-3 w-3 text-accent-foreground" />
        ) : (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground/30 opacity-0 transition-opacity group-hover/sort:opacity-100" />
        )}
      </button>
    </th>
  );

  const totalDealValue = useMemo(() => data.reduce((sum, r) => sum + r.dealSize, 0), [data]);

  // const sortedData = useMemo(() => {
  //   if (!sortKey) return data;
  //   return [...data].sort((a, b) => {
  //     let aVal: unknown = (a as any)[sortKey];
  //     let bVal: unknown = (b as any)[sortKey];
  //     // Resolve AF Lead name from adminUserList
  //     if (sortKey === 'leadRM') {
  //       aVal = adminUserList?.find(u => String(u.id) === String(a.leadRM))?.name ?? '';
  //       bVal = adminUserList?.find(u => String(u.id) === String(b.leadRM))?.name ?? '';
  //     }
  //     if (typeof aVal === 'number' && typeof bVal === 'number') {
  //       return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  //     }
  //     const aStr = String(aVal ?? '').toLowerCase();
  //     const bStr = String(bVal ?? '').toLowerCase();
  //     return sortDir === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
  //   });
  // }, [data, sortKey, sortDir, adminUserList]);

  const lenders = useMemo(
    () =>
      data.flatMap((record) =>
        (record.lenders || []).map((lender: any) => ({
          ...lender,
          ...(lenderStatusOverrides[lender.id] || {}),
          comments: lenderCommentOverrides[lender.id] ?? lender.comments,
        }))
      ),
    [data, lenderStatusOverrides, lenderCommentOverrides]
  );
  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-card rounded-xl border">
        <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center mb-4">
          <FileText className="w-7 h-7 text-muted-foreground/40" />
        </div>
        <h3 className="text-base font-semibold text-foreground mb-1">No records in Credit Evaluation</h3>
        <p className="text-muted-foreground text-sm text-center max-w-sm">Records will appear here with their tagged lenders.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.05)]">
      <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
        <div className="min-w-[1800px]">
          <table className="w-full">
            <thead className="sticky top-0 z-30 data-table-header">
              <tr className="border-b">
                <th className="frozen-column data-table-cell text-left font-medium text-muted-foreground text-[11px] tracking-wide z-40 min-w-[200px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] bg-white">
                  <button onClick={() => onSortChange?.('companyName')} className="flex items-center gap-1 bg-white transition-colors hover:text-foreground">
                    Company
                    {sortField === 'companyName' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-accent-foreground" /> : <ArrowDown className="h-3 w-3 text-accent-foreground" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-muted-foreground/30" />
                    )}
                  </button>
                </th>
                <SortableHeader field="leadRM" label="AF Lead" className="min-w-[120px]" />
                <SortableHeader field="partnerCompanyName" label="Partner Owner" className="min-w-[130px]" />
                <SortableHeader field="rating" label="Rating" className="min-w-[80px]" />
                <SortableHeader field="city" label="City" className="min-w-[100px]" />
                <th className="data-table-cell text-left font-medium text-muted-foreground text-[11px] tracking-wide bg-muted min-w-[150px]">Product</th>
                <th className="data-table-cell text-right font-medium text-muted-foreground text-[11px] tracking-wide bg-muted min-w-[110px]">
                  <button onClick={() => onSortChange?.('dealSize')} className="flex w-full items-center justify-end gap-1 transition-colors hover:text-foreground">
                    Deal Value
                    {sortField === 'dealSize' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-accent-foreground" /> : <ArrowDown className="h-3 w-3 text-accent-foreground" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-muted-foreground/30" />
                    )}
                  </button>
                </th>
                <SortableHeader field="mraStatus" label="MRA" className="min-w-[80px]" />
                <SortableHeader field="termSheetStatus" label="TS Signed" className="min-w-[90px]" />
                <SortableHeader field="priority" label="Priority" className="min-w-[100px]" />
                <SortableHeader field="pipeline" label="Status" className="min-w-[130px]" />
                <SortableHeader field="createdDate" label="Created Date" className="min-w-[110px]" />
                <SortableHeader field="next_follow_up_date" label="Next Followup" className="min-w-[110px]" />
                <th className="data-table-cell text-left font-medium text-muted-foreground text-[11px] tracking-wide bg-muted min-w-[180px]">
                  Last Interaction
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((record, index) => {
                const isOpen = openAccordionId === record.id;
                const lenders = (record.lenders || []).map((lender: any) => ({
                  ...lender,
                  ...(lenderStatusOverrides[lender.id] || {}),
                  comments: lenderCommentOverrides[lender.id] ?? lender.comments,
                }));

                return (
                  <React.Fragment key={record.id}>
                    <tr
                      className={cn(
                        'border-b transition-all duration-150 data-row cursor-pointer',
                        isOpen ? 'bg-accent/5' : index % 2 === 0 ? 'bg-card' : 'bg-muted/20'
                      )}
                      onClick={() => onRowClick(record)}
                    >
                      <td className={cn(
                        'frozen-column frozen-cell data-table-cell z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] bg-white whitespace-normal',
                        isOpen ? 'bg-white' : index % 2 === 0
                      )}>
                        <div className="flex min-w-0 items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => toggleAccordion(record.id, e)}
                            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-accent/40"
                            aria-label={isOpen ? 'Collapse' : 'Expand'}
                          >
                            <ChevronDown className={cn(
                              'h-4 w-4 text-muted-foreground transition-transform duration-150',
                              isOpen && 'rotate-180 text-accent'
                            )} />
                          </button>
                          {record.companyLogo ? (
                            <img
                              src={record.companyLogo}
                              alt={record.companyName}
                              className="w-10 h-10 rounded-md shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center shrink-0">
                              <span className="text-[10px] font-semibold text-muted-foreground">
                                {getInitials(record.companyName)}
                              </span>
                            </div>
                          )}
                          <div className="min-w-0">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="font-medium text-foreground text-sm block hover:text-accent-foreground transition-colors text-left leading-tight py-1 truncate max-w-[240px]"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/manage-customer/${record.id}`);
                                    }}
                                  >
                                    {record.companyName}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>{record.companyName}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <span className="text-[11px] text-muted-foreground">{lenders.length} lender(s)</span>
                          </div>
                        </div>
                      </td>
                      <td className="data-table-cell text-muted-foreground text-sm truncate max-w-[140px]">
                        {adminUserList?.find(a => String(a.id) === String(record.leadRM))?.name || "-"}
                      </td>
                      <td className="data-table-cell text-muted-foreground text-sm truncate max-w-[140px]">{record.partnerCompanyName}</td>
                      <td className="data-table-cell"><CreditRatingBadge rating={record.rating} /></td>
                      <td className="data-table-cell text-muted-foreground text-sm">{record.city}</td>
                      <td className="data-table-cell"><ProductsCell products={record.productTypeList} /></td>
                      <td className="data-table-cell text-right font-medium text-foreground text-sm">{formatCurrency(record.dealSize)}</td>
                      <td className="data-table-cell"><DocumentStatusBadge status={record.mraStatus} /></td>
                      <td className="data-table-cell"><DocumentStatusBadge status={record.termSheetStatus} /></td>
                      <td className="data-table-cell" onClick={(e) => e.stopPropagation()}>
                        <PriorityDropdown value={record.pipeline} onChange={(v) => onUpdateRecord?.(record.id, { pipeline: v })} />
                      </td>
                      <td className="data-table-cell" onClick={(e) => e.stopPropagation()}>
                        <StatusDropdown
                          value={record.status}
                          statusId={record.statusId}
                          statusOptions={statusOptions}
                          onChange={(status) => onUpdateRecord?.(record.id, {
                            status: status.name,
                            statusId: String(status.id),
                          })}
                        />
                      </td>
                      <td className="data-table-cell text-muted-foreground text-sm">{record.createdDate || '—'}</td>
                      <td className="data-table-cell text-muted-foreground text-sm">
                        {record.next_follow_up_date ? formatDate(record.next_follow_up_date) : <span className="text-muted-foreground/50">—</span>}
                      </td>
                      <td className="data-table-cell">
                        <button
                          onClick={(e) => { e.stopPropagation(); onInteractionClick(record); }}
                          className="flex items-center gap-2 text-left text-muted-foreground hover:text-accent-foreground transition-colors"
                        >
                          <MessageSquare className="w-3.5 h-3.5 shrink-0" />
                          <span className="text-sm">{record.comments[0] ? truncateText(record.comments[0].text, 25) : 'No interactions'}</span>
                        </button>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr className="accordion-content-row border-b bg-[#f8fcfd]">
                        <td colSpan={15} className="p-0">
                          {/* ── Tagged Lenders Panel ── */}
                          <div className="mx-3 my-2.5 rounded-xl border border-[#d0ecef] bg-white shadow-[0_4px_20px_rgba(93,152,161,0.08)] overflow-hidden">

                            {/* Header bar */}
                            {/* <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-[#eef7f8] to-[#f5fbfc] border-b border-[#d0ecef]">
                              <div className="flex items-center gap-2">
                                <div className="w-1.5 h-4 rounded-full bg-[#5D98A1]" />
                                <span className="text-[11px] font-semibold text-[#3d7a84] tracking-wide uppercase">
                                  Tagged Lenders
                                </span>
                                <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-[#5D98A1] text-white text-[10px] font-bold">
                                  {lenders.length}
                                </span>
                              </div>
                            </div> */}

                            {/* Lender sub-table */}
                            <div className="w-full max-h-[280px] overflow-auto scrollbar-thin">
                              <table className="w-full min-w-[1300px]">
                                <thead className="sticky top-0 z-10">
                                  <tr className="border-b border-[#e0f0f2] bg-[#f4fafb]">
                                    <th className="px-4 py-2.5 text-left text-[10.5px] font-semibold text-[#5D98A1] tracking-wider uppercase min-w-[150px] max-w-[180px] whitespace-normal">Institution</th>
                                    <th className="px-4 py-2.5 text-left text-[10.5px] font-semibold text-[#5D98A1] tracking-wider uppercase min-w-[120px]">Product</th>
                                    <th className="px-4 py-2.5 text-left text-[10.5px] font-semibold text-[#5D98A1] tracking-wider uppercase min-w-[150px]">Requirement ID</th>
                                    <th className="px-4 py-2.5 text-left text-[10.5px] font-semibold text-[#5D98A1] tracking-wider uppercase min-w-[130px]">Proposal Amount</th>
                                    <th className="px-4 py-2.5 text-left text-[10.5px] font-semibold text-[#5D98A1] tracking-wider uppercase min-w-[130px]">AF Lender RM</th>
                                    <th className="px-4 py-2.5 text-left text-[10.5px] font-semibold text-[#5D98A1] tracking-wider uppercase min-w-[110px]">Created</th>
                                    <th className="px-4 py-2.5 text-left text-[10.5px] font-semibold text-[#5D98A1] tracking-wider uppercase min-w-[140px]">Status</th>
                                    <th className="px-4 py-2.5 text-left text-[10.5px] font-semibold text-[#5D98A1] tracking-wider uppercase min-w-[210px]">Latest Conversation</th>
                                    <th className="px-4 py-2.5 text-left text-[10.5px] font-semibold text-[#5D98A1] tracking-wider uppercase min-w-[110px]">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {lenders.map((lender, li) => (
                                    <tr
                                      key={lender.id}
                                      className={cn(
                                        'group border-b border-[#edf6f7] last:border-0 transition-all duration-150 cursor-pointer',
                                        li % 2 === 0 ? 'bg-white' : 'bg-[#f8fcfd]',
                                        'hover:bg-[#eef7f8] hover:shadow-[inset_2px_0_0_#5D98A1]'
                                      )}
                                    >
                                      {/* Institution */}
                                      <td className="px-4 py-3 whitespace-normal">
                                        <div className="flex items-center gap-2.5">
                                          {lender.avatar ? (
                                            <div className="w-20 h-9 rounded-lg overflow-hidden border border-slate-100 bg-white flex-shrink-0 shadow-sm">
                                              <img
                                                src={lender.avatar.startsWith('http')
                                                  ? lender.avatar
                                                  : `${import.meta.env.VITE_API_BASE_URL}/${lender.avatar}`}
                                                alt={lender.institution}
                                                className="w-full h-full object-contain"
                                                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = ''; }}
                                              />
                                            </div>
                                          ) : (
                                            <div className="w-10 h-10 rounded-lg bg-[#eef7f8] border border-[#c8e8ec] flex items-center justify-center flex-shrink-0">
                                              <Building2 className="w-3.5 h-3.5 text-[#5D98A1]" />
                                            </div>
                                          )}
                                          <span className="text-[13px] font-semibold text-slate-800 group-hover:text-[#3d7a84] transition-colors">
                                            {lender.institution}
                                          </span>
                                        </div>
                                      </td>

                                      {/* Product */}
                                      <td className="px-4 py-3">
                                        <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                                          {lender.product || '—'}
                                        </span>
                                      </td>

                                      {/* Requirement ID */}
                                      <td className="px-4 py-3">
                                        <span className="font-mono text-[12px] text-slate-600 tracking-tight">
                                          {lender.requirementId || '—'}
                                        </span>
                                      </td>

                                      {/* Proposal Amount */}
                                      <td className="px-4 py-3">
                                        <span className="text-[13px] font-semibold text-slate-800">
                                          {lender.proposalAmount ? formatCurrency(lender.proposalAmount) : '—'}
                                        </span>
                                      </td>

                                      {/* AF Lender RM */}
                                      <td className="px-4 py-3 text-[12px] text-slate-500">
                                        {adminUserList?.find(user => user.id === Number(lender.lenderRM))?.name || '—'}
                                      </td>

                                      {/* Created */}
                                      <td className="px-4 py-3 text-[12px] text-slate-400">
                                        {formatDate(lender.createdDate)}
                                      </td>

                                      {/* Status — colour-coded badge */}
                                      <td className="px-4 py-3">
                                        {(() => {
                                          const statusLabel = lender.status || '—';
                                          const statusConfig = getStatusConfig(statusLabel as any);
                                          const StatusIcon = statusConfig?.icon ?? MessageSquare;
                                          return (
                                            <Badge
                                              variant={statusConfig?.variant ?? "outline"}
                                              className={cn(
                                                "inline-flex items-center gap-1.5 border text-[11px] font-medium",
                                                statusConfig?.color ?? "text-slate-600"
                                              )}
                                            >
                                              <StatusIcon className="h-3 w-3" />
                                              {statusLabel}
                                            </Badge>
                                          );
                                        })()}
                                      </td>

                                      {/* Latest Conversation */}
                                      <td className="px-4 py-3">
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setCommentPopup({ isOpen: true, comments: lender.comments || [], lenderId: lender.id });
                                          }}
                                          className="flex items-center gap-1.5 rounded-md px-2 py-1 text-slate-500 hover:text-[#3d7a84] hover:bg-[#eef7f8] transition-all"
                                        >
                                          <MessageSquare className="w-3 h-3 shrink-0 text-[#5D98A1]" />
                                          <span className="text-[11px] truncate max-w-[180px]">
                                            {lender.comments?.[lender.comments.length - 1]?.text || 'No interactions'}
                                          </span>
                                        </button>
                                      </td>

                                      {/* Actions */}
                                      <td className="px-4 py-3">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-7 px-3 text-[11px] border-[#c8e8ec] text-[#3d7a84] hover:bg-[#eef7f8] hover:border-[#5D98A1] hover:text-[#2a6370] transition-all"
                                          onClick={() => setStatusOpenFor(lender.id ?? null)}
                                        >
                                          Update
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot className="border-t bg-muted/30">
              <tr>
                <td className="frozen-column frozen-column-footer data-table-cell z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground text-sm">Total</span>
                    <span className="px-1.5 py-0.5 bg-accent/10 text-accent-foreground rounded text-[11px] font-medium">{data.length} records</span>
                  </div>
                </td>
                <td className="data-table-cell" colSpan={5}></td>
                <td className="data-table-cell text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5 text-success" />
                    <span className="font-semibold text-foreground text-sm">{formatCurrency(totalDealValue)}</span>
                  </div>
                </td>
                <td className="data-table-cell" colSpan={8}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <CommentPopup
        isOpen={commentPopup.isOpen}
        comments={commentPopup.comments || []}
        onClose={() => setCommentPopup((prev) => ({ ...prev, isOpen: false, lenderId: null }))}
        relationshipManagers={adminUserList}
        currentUser={currentUser}
        onSave={handleSaveComment}
        onResolve={handleResolveComment}
      />

      {statusOpenFor && (() => {
        const lender = lenders.find((l) => String(l.id) === String(statusOpenFor));
        if (!lender) return null;
        const currentStatus = lender.statusId ?? LenderStatus?.find((s: any) => String(s.name) === String(lender.status))?.id;

        return (
          <LenderStatusDialog
            open={!!statusOpenFor}
            onOpenChange={(v) => !v && setStatusOpenFor(null)}
            onSubmit={(vals) => handleStatusUpdate(statusOpenFor, vals)}
            isHybrid={false}
            // sanctionedAmountFromHistory={lastSanction}
            lenderInstitution={(lender as any).institutionName ?? (lender as any).institution}
            lenderName={(lender as any).lenderName}
            currentStatus={currentStatus as any}
            LenderStatus={LenderStatus}
            isLeasing={false}
          />
        );
      })()}
    </div>
  );
}
